import { useEffect, useMemo, useRef, useState } from 'react';
import { usePostOrderPrimaryPhaseStatsBatchMutation } from '../../../../../slices/statisticsApiSlice';
import { useGetAssistantUsersQuery, useGetSaleUsersQuery } from '../../../../../slices/usersApiSlice';
import { useGetCustomersQuery } from '../../../../../slices/customersApiSlice';
import { buildStatisticsQueryParams } from '../../../filters/lib/statisticsFilterParams';
import {
	buildPersistedChartFilters,
	isPipelineBuiltinChartKind,
	resolvePersistedChartIncludeOrders,
	resolvePersistedChartKind,
	PERSISTED_CHART_KIND,
} from '../lib/statisticsPersistedCharts';
import { resolveTrendMonthlySpecForQuery } from '../../../filters/lib/statisticsTrendFilters';
import { isFrozenChartSnapshot } from '../lib/statisticsChartRefreshMode';
import { resolvePersistedChartViewFilters } from '../lib/dashboardChartLifecycle';
import { resolveDashboardChartBundleEntry } from '../lib/buildDashboardChartBundle';

/** 跨挂载复用：离开首页/懒加载卸掉后短时回首页可直接出图 */
const BATCH_RESULT_CACHE_TTL_MS = 120_000;
const BATCH_RESULT_CACHE_MAX = 12;
const batchResultCache = new Map();

const buildBatchItemsCacheKey = (batchItems = []) => {
	const sortedItems = [...batchItems].sort((a, b) => a.key.localeCompare(b.key));
	return JSON.stringify(sortedItems);
};

const readBatchResultCache = (batchItems) => {
	const key = buildBatchItemsCacheKey(batchItems);
	const entry = batchResultCache.get(key);
	if (!entry) return null;
	if (entry.expiresAt <= Date.now()) {
		batchResultCache.delete(key);
		return null;
	}
	return entry;
};

const writeBatchResultCache = (batchItems, results, queryByKey) => {
	const key = buildBatchItemsCacheKey(batchItems);
	if (batchResultCache.has(key)) batchResultCache.delete(key);
	batchResultCache.set(key, {
		expiresAt: Date.now() + BATCH_RESULT_CACHE_TTL_MS,
		results: { ...results },
		queryByKey: [...queryByKey.entries()],
	});
	while (batchResultCache.size > BATCH_RESULT_CACHE_MAX) {
		const oldestKey = batchResultCache.keys().next().value;
		batchResultCache.delete(oldestKey);
	}
};

const invalidateBatchResultCache = (batchItems) => {
	batchResultCache.delete(buildBatchItemsCacheKey(batchItems));
};

/** socket 推送时清空，避免离开首页后再回来仍吃到旧快照 */
export const clearDashboardChartBatchCache = () => {
	batchResultCache.clear();
};

/** 批量查数签名：与图表展示顺序无关，避免拖拽排序触发重查 */
const buildBatchFetchSignature = (batchItems, dataRefreshToken) => {
	const sortedItems = [...batchItems].sort((a, b) => a.key.localeCompare(b.key));
	return JSON.stringify({ items: sortedItems, dataRefreshToken });
};

const shouldIncludeChartInBatch = (chart, includeOrdersChartIdSet) => {
	const chartKind = resolvePersistedChartKind(chart);
	if (chartKind === PERSISTED_CHART_KIND.OVERDUE) return false;
	/** L1 / 全屏钻取：跟页流程图也要进 batch 拉订单，否则右侧明细与点击钻取无数据 */
	if (includeOrdersChartIdSet.has(chart.id)) return true;
	/** live pipeline：仅模板跟页图不进 batch；复制/首页 pin 等独立口径图须按自身 filters 查数 */
	if (isPipelineBuiltinChartKind(chartKind)) {
		if (isFrozenChartSnapshot(chart) && chart.dataSnapshot) return false;
		if (chart.usesPageFilters) return false;
		return true;
	}
	if (isFrozenChartSnapshot(chart) && chart.dataSnapshot) {
		const frozen = chart.dataSnapshot;
		const yearMonthsBroken =
			Boolean(frozen.trendYearMonthsMode || frozen.lineCompareYearMonthsMode) &&
			!(frozen.trendPeriodYoy?.years?.length || frozen.lineComparePeriodYoy?.years?.length);
		return yearMonthsBroken;
	}
	return true;
};

/** 全年各月/各季走势：API 须返回与配置年份一致的 trendPeriodYoy */
const isYearMonthsChartStatsIncomplete = (chart, chartStats) => {
	const filters = buildPersistedChartFilters(chart);
	const monthlySpec = resolveTrendMonthlySpecForQuery(filters);
	if (!monthlySpec?.years?.length) return false;

	const yoy = chartStats?.trendPeriodYoy;
	if (!yoy) return true;

	const apiYears = yoy.years ?? [];
	const specYears = monthlySpec.years;
	if (apiYears.length !== specYears.length) return true;
	return !specYears.every((year, index) => year === apiYears[index]);
};

const stripIncludeOrders = (query = {}) => {
	const next = { ...query };
	delete next.includeOrders;
	return next;
};

const isAbortError = (error) =>
	error?.name === 'AbortError' || error?.message === 'Aborted' || Boolean(error?.aborted);

/** 仅开关 includeOrders：关掉不必重查；打开时若已有订单也不必重查 */
const canReuseCachedStatsForQuery = (item, prevQueryJson, chartStats) => {
	if (!prevQueryJson || !chartStats) return false;
	let prevQuery;
	try {
		prevQuery = JSON.parse(prevQueryJson);
	} catch {
		return false;
	}
	if (JSON.stringify(stripIncludeOrders(prevQuery)) !== JSON.stringify(stripIncludeOrders(item.query))) return false;
	if (!item.query?.includeOrders) return true;
	return Array.isArray(chartStats.orders) && chartStats.orders.length > 0;
};

const resolveItemsNeedingFetch = (items, charts, prevResults, prevQueryByKey, forceRefreshAll) => {
	if (forceRefreshAll) return items;

	return items.filter((item) => {
		const queryKey = JSON.stringify(item.query);
		const prevQueryJson = prevQueryByKey.get(item.key);
		const chartStats = prevResults[item.key];
		if (canReuseCachedStatsForQuery(item, prevQueryJson, chartStats)) return false;
		if (prevQueryJson !== queryKey) return true;
		if (!chartStats) return true;
		const chart = charts.find((entry) => entry.id === item.key);
		return chart ? isYearMonthsChartStatsIncomplete(chart, chartStats) : true;
	});
};

const pickResultsForItems = (results, items) => {
	const next = {};
	for (const item of items) {
		if (results?.[item.key]) next[item.key] = results[item.key];
	}
	return next;
};

/** 单图 loading：仅该图正在查、或全屏钻取拉订单时显示，避免其它图联动闪烁 */
const resolveChartBatchLoadingFlags = (
	chart,
	{ results, fetchingKeySet, includeOrdersChartIdSet, batchPendingWithoutStats = false },
) => {
	const inBatch = shouldIncludeChartInBatch(chart, includeOrdersChartIdSet);
	const forceLive = includeOrdersChartIdSet.has(chart.id);
	const chartStats = results[chart.id] ?? null;
	const hasCachedStats = Boolean(chartStats);
	const yearMonthsIncomplete = isYearMonthsChartStatsIncomplete(chart, chartStats);
	const isFetchingThisChart = fetchingKeySet.has(chart.id);

	if (!inBatch) {
		return { isStatisticsQueryLoading: false, isStatisticsQueryFetching: false, forceLive };
	}

	const isStatisticsQueryLoading =
		(isFetchingThisChart && !hasCachedStats) || (batchPendingWithoutStats && !hasCachedStats);
	const isStatisticsQueryFetching =
		(forceLive && isFetchingThisChart && !chartStats?.orders?.length) ||
		(isFetchingThisChart && yearMonthsIncomplete) ||
		(batchPendingWithoutStats && !hasCachedStats && yearMonthsIncomplete);

	return { isStatisticsQueryLoading, isStatisticsQueryFetching, forceLive };
};

const useDashboardChartsBatch = (
	charts = [],
	{
		isManager = false,
		overdueData,
		isOverdueLoading,
		enabled = true,
		dataRefreshToken = 0,
		includeOrdersChartIds = [],
		statisticsSourceSnapshotsById = {},
		fetchCustomers = true,
		includeCompares = true,
		pageAppliedFilters = null,
	} = {},
) => {
	const { data: fetchedSaleUsers = [] } = useGetSaleUsersQuery({ scope: 'order' }, { skip: !enabled });
	const { data: fetchedAssistantUsers = [] } = useGetAssistantUsersQuery({ scope: 'order' }, { skip: !enabled });
	const { data: fetchedCustomers = [] } = useGetCustomersQuery(undefined, { skip: !enabled || !isManager || !fetchCustomers });
	const [postBatch] = usePostOrderPrimaryPhaseStatsBatchMutation();
	const postBatchRef = useRef(postBatch);
	postBatchRef.current = postBatch;
	const lastSignatureRef = useRef('');
	const pendingSignatureRef = useRef('');
	const batchRequestIdRef = useRef(0);
	const inFlightRef = useRef(null);
	const lastDataRefreshTokenRef = useRef(dataRefreshToken);
	const prevQueryByKeyRef = useRef(new Map());
	const batchResultsRef = useRef(null);
	const [batchResults, setBatchResults] = useState(null);
	const [fetchingChartIds, setFetchingChartIds] = useState(() => []);
	/** 当前 batchSignature 是否已结束（成功/失败/无需请求），用于避免「未开始查数」时骨架提前关掉 */
	const [settledBatchSignature, setSettledBatchSignature] = useState('');
	batchResultsRef.current = batchResults;

	const includeOrdersChartIdSet = useMemo(() => new Set(includeOrdersChartIds.filter(Boolean)), [includeOrdersChartIds]);
	const fetchingKeySet = useMemo(() => new Set(fetchingChartIds), [fetchingChartIds]);

	const batchItems = useMemo(
		() =>
			charts
				.filter((chart) => shouldIncludeChartInBatch(chart, includeOrdersChartIdSet))
				.map((chart) => ({
					key: chart.id,
					query: buildStatisticsQueryParams(resolvePersistedChartViewFilters(chart, pageAppliedFilters), {
						includeOrders: resolvePersistedChartIncludeOrders(chart) || includeOrdersChartIdSet.has(chart.id),
						compositionCompareSeries: Boolean(chart?.chartConfig?.compareSeries),
						includeCompares,
					}),
				})),
		[charts, includeCompares, includeOrdersChartIdSet, pageAppliedFilters],
	);
	const batchItemsRef = useRef(batchItems);
	batchItemsRef.current = batchItems;
	const chartsRef = useRef(charts);
	chartsRef.current = charts;

	const batchSignature = useMemo(
		() => buildBatchFetchSignature(batchItems, dataRefreshToken),
		[batchItems, dataRefreshToken],
	);

	useEffect(() => {
		const items = batchItemsRef.current;
		if (!enabled || !items.length) {
			setBatchResults(null);
			setFetchingChartIds([]);
			setSettledBatchSignature('');
			prevQueryByKeyRef.current = new Map();
			lastSignatureRef.current = '';
			return undefined;
		}

		if (batchSignature === lastSignatureRef.current) {
			return undefined;
		}

		const signature = batchSignature;
		const forceRefreshAll = dataRefreshToken !== lastDataRefreshTokenRef.current;
		lastDataRefreshTokenRef.current = dataRefreshToken;

		if (forceRefreshAll) {
			invalidateBatchResultCache(items);
		}

		const cached = forceRefreshAll ? null : readBatchResultCache(items);
		if (cached?.queryByKey?.length && prevQueryByKeyRef.current.size === 0) {
			prevQueryByKeyRef.current = new Map(cached.queryByKey);
		}

		const prevResults = {
			...(cached?.results ?? {}),
			...(batchResultsRef.current?.results ?? {}),
		};
		const itemsToFetch = resolveItemsNeedingFetch(
			items,
			chartsRef.current,
			prevResults,
			prevQueryByKeyRef.current,
			forceRefreshAll,
		);

		const retainedResults = {};
		for (const item of items) {
			if (prevResults[item.key]) {
				retainedResults[item.key] = prevResults[item.key];
			}
		}

		for (const item of items) {
			prevQueryByKeyRef.current.set(item.key, JSON.stringify(item.query));
		}
		lastSignatureRef.current = signature;
		pendingSignatureRef.current = signature;
		setSettledBatchSignature('');

		// 只保留当前 batch 里的图，避免切图后上一张的订单明细还挂在 results 上
		const mergedRetained = pickResultsForItems(retainedResults, items);
		setBatchResults({ results: mergedRetained });

		if (itemsToFetch.length === 0) {
			setFetchingChartIds([]);
			setSettledBatchSignature(signature);
			writeBatchResultCache(items, mergedRetained, prevQueryByKeyRef.current);
			return undefined;
		}

		const requestId = batchRequestIdRef.current + 1;
		batchRequestIdRef.current = requestId;
		const fetchingKeys = itemsToFetch.map((item) => item.key);
		setFetchingChartIds(fetchingKeys);

		const runBatch = async (attempt = 0) => {
			const trigger = postBatchRef.current(itemsToFetch);
			inFlightRef.current = trigger;
			const data = await trigger.unwrap();
			if (inFlightRef.current === trigger) inFlightRef.current = null;
			if (batchRequestIdRef.current !== requestId || pendingSignatureRef.current !== signature) return;

			const fetchedResults = data?.results ?? {};
			const mergedResults = pickResultsForItems({ ...mergedRetained, ...fetchedResults }, items);
			setBatchResults({ results: mergedResults });
			writeBatchResultCache(items, mergedResults, prevQueryByKeyRef.current);

			const hasIncompleteYearMonths = itemsToFetch.some((item) => {
				const chart = chartsRef.current.find((entry) => entry.id === item.key);
				return chart && isYearMonthsChartStatsIncomplete(chart, mergedResults[item.key]);
			});
			if (hasIncompleteYearMonths && attempt < 1) {
				return runBatch(attempt + 1);
			}
		};

		runBatch(0)
			.catch((error) => {
				if (isAbortError(error)) return;
				if (batchRequestIdRef.current !== requestId || pendingSignatureRef.current !== signature) return;
				// 保留已有结果，避免网络抖动时首页图表集体空白
				lastSignatureRef.current = '';
				setBatchResults({ results: mergedRetained });
			})
			.finally(() => {
				if (batchRequestIdRef.current === requestId) {
					setFetchingChartIds([]);
					if (pendingSignatureRef.current === signature) {
						setSettledBatchSignature(signature);
					}
				}
			});

		return () => {
			// 切图 / StrictMode 重挂载：丢掉过时签名并取消进行中的请求，避免叠加重查询
			lastSignatureRef.current = '';
			pendingSignatureRef.current = '';
			batchRequestIdRef.current += 1;
			inFlightRef.current?.abort?.();
			inFlightRef.current = null;
		};
	}, [batchSignature, dataRefreshToken, enabled]);

	const hydratedBatchResults = useMemo(() => {
		if (!enabled || batchItems.length === 0) return null;
		const cached = readBatchResultCache(batchItems);
		const merged = pickResultsForItems(
			{
				...(cached?.results ?? {}),
				...(batchResults?.results ?? {}),
			},
			batchItems,
		);
		if (Object.keys(merged).length === 0) {
			return batchResults == null && !cached?.results ? null : { results: {} };
		}
		return { results: merged };
	}, [batchItems, batchResults, enabled]);

	const bundleByChartId = useMemo(() => {
		const results = hydratedBatchResults?.results ?? {};
		const awaitingInitialBatch = batchItems.length > 0 && hydratedBatchResults == null;
		const signaturePending = Boolean(enabled && batchItems.length > 0 && batchSignature !== lastSignatureRef.current);
		const batchSettled = settledBatchSignature === batchSignature;
		const displayFetchingKeySet =
			awaitingInitialBatch || signaturePending
				? new Set([...fetchingKeySet, ...batchItems.map((item) => item.key)])
				: fetchingKeySet;
		const batchPendingWithoutStats =
			enabled && batchItems.length > 0 && !batchSettled && (awaitingInitialBatch || signaturePending || fetchingKeySet.size > 0);
		const bundles = {};

		for (const chart of charts) {
			const loadingFlags = resolveChartBatchLoadingFlags(chart, {
				results,
				fetchingKeySet: displayFetchingKeySet,
				includeOrdersChartIdSet,
				batchPendingWithoutStats,
			});

			bundles[chart.id] = resolveDashboardChartBundleEntry({
				snapshot: chart,
				primaryPhaseStats: results[chart.id] ?? null,
				overdueData,
				isOverdueLoading,
				fetchedSaleUsers,
				fetchedAssistantUsers,
				fetchedCustomers,
				isStatisticsQueryLoading: loadingFlags.isStatisticsQueryLoading,
				isStatisticsQueryFetching: loadingFlags.isStatisticsQueryFetching,
				forceLive: loadingFlags.forceLive,
				statisticsSourceSnapshotsById,
			});
		}

		return bundles;
	}, [
		batchItems,
		batchSignature,
		charts,
		enabled,
		fetchedAssistantUsers,
		fetchedCustomers,
		fetchedSaleUsers,
		fetchingKeySet,
		hydratedBatchResults,
		includeOrdersChartIdSet,
		isOverdueLoading,
		overdueData,
		settledBatchSignature,
		statisticsSourceSnapshotsById,
	]);

	return { bundleByChartId, isBatchLoading: fetchingChartIds.length > 0 || (batchItems.length > 0 && settledBatchSignature !== batchSignature) };
};

export default useDashboardChartsBatch;
